import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Stack,
    Text,
    Textarea
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import SelectCountry from '../SelectCountry/SelectCountry';
import { useAuth } from "../../../../shared/providers/AuthProvider/AuthProvider";
import InlineErrorInput from "../../../../shared/components/InlineErrorInput/InlineErrorInput";
import isEqual from 'lodash.isequal';
import { useAppToast } from "../../../../hooks/useAppToast";

const PrivateAccountForm = ({ onSuccess, config }) => {
    const { t } = useTranslation('default');
    const { setup } = useAuth();
    const toast = useAppToast();

    const initialValues = {
        firstName: config?.firstName || '',
        lastName: config?.lastName || '',
        country: config?.country || '',
        postalCode: config?.postalCode || '',
        city: config?.city || '',
        street: config?.street || '',
        building: config?.building || '',
        apartment: config?.apartment || '',
        comments: config?.comments || '',
        payment: config?.payment || '',
        paymentSecret: config?.paymentSecret || ''
    };

    const validationSchema = Yup.object({
        firstName: Yup.string().min(2, t('setup.form.firstName.min')).required(t('setup.form.firstName.required')),
        lastName: Yup.string().min(2, t('setup.form.lastName.min')).required(t('setup.form.lastName.required')),
        country: Yup.string().required(t('setup.form.country.required')),
        postalCode: Yup.string().required(t('setup.form.postalCode.required')),
        city: Yup.string().required(t('setup.form.city.required')),
        street: Yup.string().required(t('setup.form.street.required')),
        building: Yup.string().required(t('setup.form.building.required')),
        payment: Yup.string(),
        paymentSecret: Yup.string(),
    });

    return (

        <Formik
            enableReinitialize
            validateOnBlur={true}
            validateOnChange={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
                try {
                    const success = await setup({ accountType: 'private', ...values });
                    if (success) {
                        onSuccess();
                    }
                } catch (e) {
                    toast({
                        title: 'Błąd podczas zapisu danych',
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                    });
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched, isValid, dirty }) => (
                <Form>

                    <Flex flexDirection={"column"} gap={'48px'} borderRadius={'12px'}>
                        <Flex flexDirection={"column"} borderRadius={'12px'}>
                            <Flex gap={'32px'} flexDirection={{ base: "column", sm: "row" }}>
                                <Box flex="1" gap={'32px'} bgColor={"white"} pb="32px" pt={'24px'} px={'24px'} borderRadius={'12px'}>
                                    <Flex flexDirection={"column"} className="header" pb={'32px'}>
                                        <Text className="title">
                                            {t('setup.form.basic.title')}
                                        </Text>
                                        <Text className="description">
                                            {t('setup.form.basic.description')}
                                        </Text>
                                    </Flex>

                                    <Stack spacing={4}>

                                        <InlineErrorInput name="firstName" label={t('setup.form.firstName.label')} value={values.firstName}
                                            onChange={handleChange} onBlur={handleBlur} error={errors.firstName} touched={touched.firstName} isRequired
                                        />

                                        <InlineErrorInput name="lastName" label={t('setup.form.lastName.label')} value={values.lastName}
                                            onChange={handleChange} onBlur={handleBlur} error={errors.lastName} touched={touched.lastName} isRequired
                                        />

                                        <FormControl isInvalid={errors.country && touched.country} isRequired={true}>
                                            <FormLabel>{t('setup.form.country.label')}</FormLabel>
                                            <SelectCountry value={values.country}
                                                onChange={(val) => {
                                                    setFieldValue('country', val, true);
                                                }}
                                            />
                                            <FormErrorMessage>{errors.country}</FormErrorMessage>
                                        </FormControl>

                                        <Flex gap={4}>
                                            <InlineErrorInput name="postalCode" label={t('setup.form.postalCode.label')} value={values.postalCode}
                                                onChange={handleChange} onBlur={handleBlur} error={errors.postalCode} touched={touched.postalCode} isRequired
                                            />
                                            <InlineErrorInput name="city" label={t('setup.form.city.label')} value={values.city}
                                                onChange={handleChange} onBlur={handleBlur} error={errors.city} touched={touched.city} isRequired
                                            />
                                        </Flex>

                                        <Flex gap={4} alignItems={"flex-end"}>
                                            <Box flex="3">
                                                <InlineErrorInput name="street" label={t('setup.form.street.label')} value={values.street}
                                                    onChange={handleChange} onBlur={handleBlur} error={errors.street} touched={touched.street} isRequired
                                                />
                                            </Box>
                                            <Box flex="1">
                                                <InlineErrorInput name="building" label={t('setup.form.building.label')} value={values.building}
                                                    onChange={handleChange} onBlur={handleBlur} error={errors.building} touched={touched.building} isRequired
                                                />
                                            </Box>
                                            <Box flex="1">
                                                <InlineErrorInput name="apartment" label={t('setup.form.apartment.label')} value={values.apartment}
                                                    onChange={handleChange} onBlur={handleBlur} error={errors.apartment} touched={touched.apartment}
                                                />
                                            </Box>
                                        </Flex>
                                    </Stack>
                                </Box>
                                <Box flex="1" borderRadius={'12px'} justifyContent={"space-between"}>
                                    <Flex flexDirection={"column"} gap={'32px'}>
                                        <Flex flexDirection={"column"} bgColor={"white"} pb="32px" pt={'24px'} px={'24px'} >
                                            <Flex flexDirection={"column"} className="header" pb={'32px'}>
                                                <Text className="title">
                                                    {t('setup.form.additional.title')}
                                                </Text>
                                                <Text className="description">
                                                    {t('setup.form.additional.description')}
                                                </Text>
                                            </Flex>
                                            <FormControl>
                                                <FormLabel className="comments">
                                                    <Text className="title">
                                                        {t('setup.form.invoice.notes.label')}
                                                    </Text>
                                                </FormLabel>
                                                <Textarea name="comments" value={values.comments} onChange={handleChange} onBlur={handleBlur} />
                                            </FormControl>
                                        </Flex>
                                        <Flex flexDirection={"column"} bgColor={"white"} pb="32px" pt={'24px'} px={'24px'} >
                                            <Flex flexDirection={"column"} className="header" pb={'32px'}>
                                                <Text className="title">
                                                    {t('setup.form.payment-integration.title')}
                                                </Text>
                                                <Text className="description">
                                                    {t('setup.form.payment-integration.description')}
                                                </Text>
                                            </Flex>
                                            <Flex flexDirection="column" gap="16px">
                                                <InlineErrorInput name="payment" label={t('setup.form.payment-integration.label')} value={values.payment}
                                                    placeholder={t('setup.form.payment-integration.placeholder')}
                                                    onChange={handleChange} onBlur={handleBlur} error={errors.payment} touched={touched.payment}
                                                />
                                                <InlineErrorInput name="paymentSecret" label={t('setup.form.payment-integration.secret.label')} value={values.paymentSecret}
                                                    placeholder={t('setup.form.payment-integration.secret.placeholder')}
                                                    onChange={handleChange} onBlur={handleBlur} error={errors.paymentSecret} touched={touched.paymentSecret}
                                                />
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                </Box>
                            </Flex>
                        </Flex>
                        <Flex w={"full"} justify={"center"}>
                            <Button type="submit" isDisabled={!isValid || isEqual(values, initialValues)} bg="brand.500" _hover={{ bg: 'brand.600' }} h={'56px'}>
                                <Text mr={'10px'}>
                                    {t('setup.form.submit')}
                                </Text>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 5.66667V8M8 8V10.3333M8 8H10.3333M8 8H5.66667M15 8C15 8.91925 14.8189 9.82951 14.4672 10.6788C14.1154 11.5281 13.5998 12.2997 12.9497 12.9497C12.2997 13.5998 11.5281 14.1154 10.6788 14.4672C9.82951 14.8189 8.91925 15 8 15C7.08075 15 6.1705 14.8189 5.32122 14.4672C4.47194 14.1154 3.70026 13.5998 3.05025 12.9497C2.40024 12.2997 1.88463 11.5281 1.53284 10.6788C1.18106 9.82951 1 8.91925 1 8C1 6.14348 1.7375 4.36301 3.05025 3.05025C4.36301 1.7375 6.14348 1 8 1C9.85652 1 11.637 1.7375 12.9497 3.05025C14.2625 4.36301 15 6.14348 15 8Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Button>
                        </Flex>
                    </Flex>

                </Form>
            )}
        </Formik>
    );
};

export default PrivateAccountForm;
